import React, { useState, useEffect } from 'react';
import { useSidebarWidth } from '../Utility/useSidebarWidth';
import {
  Box, Typography, TextField, InputAdornment,
  Grid, Paper, Button, FormControl, Select, MenuItem, Divider,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon    from '@mui/icons-material/Search';
import SettingsIcon  from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MenuBookIcon  from '@mui/icons-material/MenuBook';
import EmailIcon     from '@mui/icons-material/Email';
import PhoneIcon     from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/* ─── API URL ─────────────────────────────────────────────────────────────── */
const SUPPORT_API = 'https://test.realatixcrm.com/api/v1/supports';

/* ─── Categories ─────────────────────────────────────────────────────────── */
const categories = [
  {
    icon: <SettingsIcon sx={{ fontSize: 22, color: '#3b82f6' }} />,
    iconBg: '#eff6ff',
    title: 'Technical Support',
    desc: 'Troubleshoot system issues and optimize your workflow performance.',
    value: 'technical',
  },
  {
    icon: <CreditCardIcon sx={{ fontSize: 22, color: '#f59e0b' }} />,
    iconBg: '#fffbeb',
    title: 'Billing & Account',
    desc: 'Manage your premium subscriptions, invoices, and payment methods.',
    value: 'billing',
  },
  {
    icon: <MenuBookIcon sx={{ fontSize: 22, color: '#f59e0b' }} />,
    iconBg: '#fffbeb',
    title: 'Product Guides',
    desc: 'Step-by-step masterclasses on leveraging advanced CRM tools.',
    value: 'general',
  },
];

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const SupportPage = () => {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  /* ── user from localStorage ── */
  const [userlogin, setUserlogin] = useState(null);
  const [user,      setUser]      = useState(null);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user'));
    if (u) { setUser(u); setUserlogin(u); }
  }, []);

  /* ── form state ── */
  const [subject,     setSubject]     = useState('');
  const [category,    setCategory]    = useState('technical');
  const [priority,    setPriority]    = useState('Medium');
  const [description, setDescription] = useState('');

  /* ── submission state ── */
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [snackbar,  setSnackbar]  = useState({ open: false, msg: '', sev: 'success' });

  /* ── colors ── */
  const bg      = isDark ? '#0f1117' : '#f0f2f5';
  const cardBg  = isDark ? '#1a1d2e' : '#ffffff';
  const border  = isDark ? '#2a2f45' : '#e2e8f0';
  const txtPri  = isDark ? '#f1f5f9' : '#0f172a';
  const txtSec  = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? '#0f1117' : '#ffffff';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px', fontSize: 13, backgroundColor: inputBg,
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#3b82f6' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    },
    '& .MuiInputBase-input::placeholder': { color: txtSec, opacity: 1 },
  };

  const selectSx = {
    borderRadius: '8px', fontSize: 13, backgroundColor: inputBg,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
  };

  /* ── Submit handler ── */
  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      setSnackbar({ open: true, msg: 'Please fill in Subject and Description.', sev: 'warning' });
      return;
    }

    setLoading(true);

    // بنجيب الـ user_URL من الـ browser
    const user_URL = window.location.href;

    // بنجيب اسم وإيميل الـ user من الـ localStorage
    const user_name  = userlogin?.data?.name  || userlogin?.name  || '';
    const user_Email = userlogin?.data?.email || userlogin?.email || '';

    const body = {
      user_name,
      user_Email,
      status: true,
      user_URL,
      subject:     subject.trim(),
      categoury:   category,          // spelling كما في الـ API
      Description: description.trim(),
    };

    console.log('📤 Sending support ticket:', body);

    try {
      const res = await fetch(SUPPORT_API, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });

      const data = await res.json();
      console.log('📥 Response:', data);

      if (res.ok) {
        setSuccess(true);
        setSubject('');
        setDescription('');
        setCategory('technical');
        setPriority('Medium');
        setSnackbar({ open: true, msg: 'Ticket submitted successfully! We\'ll get back to you soon.', sev: 'success' });
      } else {
        const errMsg = data?.message || data?.error || 'Failed to submit ticket. Please try again.';
        setSnackbar({ open: true, msg: errMsg, sev: 'error' });
      }
    } catch (err) {
      console.error('❌ Support ticket error:', err);
      setSnackbar({ open: true, msg: 'Network error. Please check your connection.', sev: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNewTicket = () => {
    setSuccess(false);
    setSubject('');
    setDescription('');
    setCategory('technical');
    setPriority('Medium');
  };

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{
      position: 'fixed',
      top: '50px',
      left: sidebarWidth,
      right: 0,
      bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: bg,
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
    }}>

      {/* Breadcrumb */}
      <div style={{
        padding: '14px 28px', flexShrink: 0,
        backgroundColor: isDark ? '#1a1d2e' : bg,
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '13px', borderBottom: `1px solid ${border}`,
      }}>
        <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Home</span>
        <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
        <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Support</span>
        {userlogin && (
          <>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db', marginLeft: 'auto' }} />
            <span style={{
              marginLeft: 'auto', fontSize: 12,
              color: isDark ? '#6b7280' : '#94a3b8',
            }}>
              Logged in as{' '}
              <strong style={{ color: isDark ? '#e5e7eb' : '#374151' }}>
                {userlogin?.data?.name || userlogin?.name || 'User'}
              </strong>
            </span>
          </>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: txtPri, mb: 1, letterSpacing: -0.5 }}>
              How can we help you?
            </Typography>
            <Typography sx={{ fontSize: 14, color: txtSec, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
              Access expert documentation, manage your account, or connect with our executive support team.
            </Typography>
          </Box>

          {/* Category Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {categories.map((cat) => (
              <Grid item xs={12} sm={6} md={4} key={cat.title}>
                <Paper
                  elevation={0}
                  onClick={() => setCategory(cat.value)}
                  sx={{
                    p: 2.5, borderRadius: '12px', cursor: 'pointer', height: '100%',
                    border: `1px solid ${category === cat.value ? '#3b82f6' : border}`,
                    background: category === cat.value
                      ? (isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.04)')
                      : cardBg,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      transform: 'translateY(-2px)',
                      boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.3)' : '0 8px 24px rgba(37,99,235,0.1)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '10px', mb: 2,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : cat.iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {cat.icon}
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: txtPri, mb: 0.75 }}>
                    {cat.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: txtSec, lineHeight: 1.55 }}>
                    {cat.desc}
                  </Typography>
                  {category === cat.value && (
                    <Box sx={{
                      mt: 1.5, display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      px: 1.25, py: 0.25, borderRadius: 10,
                      background: isDark ? 'rgba(59,130,246,0.2)' : '#eff6ff',
                      color: '#3b82f6', fontSize: 11, fontWeight: 700,
                    }}>
                      ✓ Selected
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Bottom Section */}
          <Grid container spacing={2.5} alignItems="flex-start">

            {/* ── Ticket Form ── */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 3.5, borderRadius: '14px', border: `1px solid ${border}`, background: cardBg }}>

                {/* ── Success State ── */}
                {success ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: '50%',
                      background: 'rgba(34,197,94,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 2,
                    }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: txtPri, mb: 0.75 }}>
                      Ticket Submitted!
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: txtSec, mb: 3, lineHeight: 1.6 }}>
                      Your request has been received. Our team will review it and get back to you at{' '}
                      <strong style={{ color: txtPri }}>
                        {userlogin?.data?.email || userlogin?.email || 'your email'}
                      </strong>.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleNewTicket}
                      sx={{
                        borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                        borderColor: '#3b82f6', color: '#3b82f6',
                        '&:hover': { background: 'rgba(59,130,246,0.06)', borderColor: '#2563eb' },
                      }}
                    >
                      Submit Another Ticket
                    </Button>
                  </Box>
                ) : (
                  /* ── Form State ── */
                  <>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: txtPri, mb: 0.5 }}>
                      Create Support Ticket
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: txtSec, mb: 3, lineHeight: 1.55 }}>
                      Describe your issue in detail and our executive response team will prioritize your request.
                    </Typography>

                    {/* Auto-filled user info banner */}
                    {userlogin && (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.5, mb: 2.5, borderRadius: '8px',
                        background: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
                        border: `1px solid ${isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.15)'}`,
                      }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(userlogin?.data?.name || userlogin?.name || 'U').charAt(0).toUpperCase()}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                            {userlogin?.data?.name || userlogin?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: txtSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userlogin?.data?.email || userlogin?.email}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: isDark ? '#6b7280' : '#94a3b8', flexShrink: 0 }}>
                          Auto-filled
                        </Typography>
                      </Box>
                    )}

                    {/* Subject */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: txtPri, mb: 0.75 }}>
                        Subject <span style={{ color: '#ef4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth size="small"
                        placeholder="e.g. Discrepancy in property valuation reports"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        sx={inputSx}
                      />
                    </Box>

                    {/* Category + Priority */}
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: txtPri, mb: 0.75 }}>Category</Typography>
                        <FormControl fullWidth size="small">
                          <Select value={category} onChange={(e) => setCategory(e.target.value)} sx={selectSx}>
                            <MenuItem value="technical">Technical Issue</MenuItem>
                            <MenuItem value="billing">Billing</MenuItem>
                            <MenuItem value="general">General</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: txtPri, mb: 0.75 }}>Priority</Typography>
                        <FormControl fullWidth size="small">
                          <Select value={priority} onChange={(e) => setPriority(e.target.value)} sx={selectSx}>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Critical">Critical</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Description */}
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: txtPri, mb: 0.75 }}>
                        Description <span style={{ color: '#ef4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth multiline rows={5}
                        placeholder="Provide as much detail as possible, including steps to reproduce..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={inputSx}
                      />
                      <Typography sx={{ fontSize: 11, color: txtSec, mt: 0.75, textAlign: 'right' }}>
                        {description.length} characters
                      </Typography>
                    </Box>

                    {/* Submit */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || !subject.trim() || !description.trim()}
                        startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{
                          background: '#2563eb', borderRadius: '8px', px: 3.5, py: 1.1,
                          textTransform: 'none', fontWeight: 700, fontSize: 14, letterSpacing: 0.2,
                          minWidth: 140,
                          '&:hover': { background: '#1d4ed8' },
                          '&.Mui-disabled': {
                            background: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                            color: txtSec,
                          },
                        }}
                      >
                        {loading ? 'Sending...' : 'Submit Ticket'}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Direct Contact */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', background: '#1e3a8a', border: 'none' }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#ffffff', mb: 2.5 }}>
                    Direct Contact
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <EmailIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 0.2 }}>Email Support</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>support@Realatixcrm.com</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 0.2 }}>Phone Support</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>+971503499171</Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Operating Hours */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: `1px solid ${border}`, background: cardBg }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: txtPri }}>Operating Hours</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 13, color: txtSec }}>Mon - Fri</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: txtPri }}>11:00 AM - 5:00 PM</Typography>
                  </Box>
                </Paper>

                {/* What happens next */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: `1px solid ${border}`, background: cardBg }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: txtPri, mb: 1.5 }}>
                    What happens next?
                  </Typography>
                  {[
                    { step: '1', text: 'We receive and review your ticket' },
                    { step: '2', text: 'A specialist is assigned within 2 hours' },
                    { step: '3', text: 'You receive an email update on progress' },
                  ].map((item) => (
                    <Box key={item.step} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.25 }}>
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#3b82f6',
                      }}>
                        {item.step}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: txtSec, lineHeight: 1.5, pt: 0.25 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <footer style={{
            marginTop: '32px', paddingTop: '16px',
            borderTop: `1px solid ${border}`,
            color: txtSec, fontSize: '12px', textAlign: 'center',
          }}>
            © 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.
          </footer>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};
